import axios from "axios";
import {
  ILoginRequestData,
  IUserInfo,
  SignInResultType,
} from "../types/authTypes";

declare let window;
axios.defaults.baseURL = window.APP_CONFIG.REACT_APP_BACKEND_URL;

const loginPageUrls = {
  patient: "/loginpatient",
  clinician: "/loginclinician",
  admin: "/loginadmin",
};

interface LoginResult {
  result: SignInResultType;
  sessionExpires?: string;
}

/**
 * Service class for communication between backend and frontend.
 */
class AuthService {
  /**Login method sends POST-Request to backend api.
   * @param loginData Login data
   * @param userType {string} patient || clinician || admin
   */
  async login(loginData: ILoginRequestData): Promise<LoginResult> {
    const user = loginData.userType ?? "";
    if (!["patient", "clinician", "admin"].includes(user)) {
      throw new Error("login() called with invalid userType");
    }
    const loginApiUrls = {
      admin: "/api/v2/session",
      clinician: "/api/v2/session",
      patient: "/api/v2/session",
    };
    const url = loginApiUrls[user];
    const loginDataJson = JSON.stringify(loginData);
    sessionStorage.setItem("usr", user);

    let response;
    let signInResult: SignInResultType = "error";

    try {
      response = await axios.post(url, loginDataJson, {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response?.status === 200) {
        signInResult = "success";
        sessionStorage.setItem("auth", "true");

        return {
          result: signInResult,
          sessionExpires: response.data.session_expires,
        };
      }
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 401) {
          signInResult = "credentials_wrong";
        } else if (err.response?.status === 403) {
          signInResult = "no_permissions";
        } else {
          signInResult = "error";
        }
      } else {
        signInResult = "error";
      }
    }

    return { result: signInResult };
  }

  getUserInfo() {
    const json = sessionStorage.getItem("userInfo");
    if (json) {
      return JSON.parse(json);
    }
  }

  setUserInfo(data: IUserInfo) {
    sessionStorage.setItem("userInfo", JSON.stringify(data));
  }

  /**
   * Returns true if the user is logged in, else false
   * @returns
   */
  isLoggedIn(): boolean {
    return sessionStorage.getItem("auth") === "true";
  }

  /**Logout Method sends DELETE-request to backend api.
   * After the DELETE-Request the user would be redirect to the right login.
   */
  async logout() {
    const apiUrl = "/api/v2/session";
    try {
      await axios.delete(apiUrl, { withCredentials: true });
    } catch (err) {
      console.warn(
        "Error when deleting the refresh token: ",
        err,
        "This may be totally fine if the token already got deleted",
      );
    } finally {
      const userRole = sessionStorage.getItem("usr");
      const loginPageUrl =
        loginPageUrls[userRole ?? "randomStringToSatisfyTypescript"];

      sessionStorage.removeItem("auth");
      sessionStorage.removeItem("usr");

      if (loginPageUrl) {
        window.location.replace(loginPageUrl);
      } else {
        console.error("No login page URL found for role:", userRole);
        window.alert("Der Nutzer konnte nicht identifiziert werden.");
        window.location.replace("/");
      }
    }
  }
}

const authService = new AuthService();
export default authService;
